import React from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsNewCmdbArchitecture } from '@atlassian/jira-servicedesk-cmdb-architecture/src/controllers/index.tsx';
import { CmdbAsyncActionsContainer } from '@atlassian/jira-servicedesk-cmdb-async-actions/src/ui/index.tsx';
import { APP_NAMES } from '@atlassian/jira-servicedesk-common/src/utils/app-names/index.tsx';
import { ObjectDetailsRenderingContext } from '@atlassian/jira-servicedesk-insight-object-details/src/ui/types.tsx';
import ObjectView from '@atlassian/jira-servicedesk-insight-object-view/src/main.tsx';
import { toCmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import {
	createObjectDetailUrl,
	getRootPath,
	isInsightRoute,
} from '@atlassian/jira-servicedesk-insight-urls/src/index.tsx';
import ServiceDeskAppBase from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/app-base/index.tsx';
import {
	AssetsAppBase,
	InsightAppBaseV2,
} from '@atlassian/jira-spa-assets-app-base/src/ui/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { Redirect, usePathParam, useRouter } from '@atlassian/react-resource-router';
import type { InsightObjectDetailsResponse } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import { InsightObjectViewPageContainer } from './ui/index.tsx';
import { FullPageObjectViewWrapper } from './ui/styled.tsx';

export const ConditionalObjectViewPage = ({
	workspaceId,
	objectId,
	prefetchData,
}: {
	workspaceId: string;
	objectId: string;
	prefetchData: InsightObjectDetailsResponse;
}) => {
	const noop = () => undefined;
	const isNewCmdbArchitecture = useIsNewCmdbArchitecture();

	return isNewCmdbArchitecture ? (
		<CmdbAsyncActionsContainer workspaceId={workspaceId}>
			<ObjectView
				context={ObjectDetailsRenderingContext.Page}
				objectId={objectId}
				workspaceId={workspaceId}
				initialObjectDetailsData={prefetchData}
				redirectOnDelete
				onUpdate={noop}
				onDelete={noop}
				onClone={noop}
			/>
		</CmdbAsyncActionsContainer>
	) : (
		<ObjectView
			context={ObjectDetailsRenderingContext.Page}
			objectId={objectId}
			workspaceId={workspaceId}
			initialObjectDetailsData={prefetchData}
			redirectOnDelete
			onUpdate={noop}
			onDelete={noop}
			onClone={noop}
		/>
	);
};

export const LegacyCmdbObjecV2View = () => {
	return (
		<UFOSegment name="cmdb-object">
			<ServiceDeskAppBase appName={APP_NAMES.INSIGHT_OBJECT_VIEW}>
				<InsightAppBaseV2>
					{({ workspaceId }) => (
						<FullPageObjectViewWrapper>
							<InsightObjectViewPageContainer>
								{({ prefetchData, objectId }) => (
									<ConditionalObjectViewPage
										workspaceId={workspaceId}
										objectId={objectId}
										prefetchData={prefetchData}
									/>
								)}
							</InsightObjectViewPageContainer>
						</FullPageObjectViewWrapper>
					)}
				</InsightAppBaseV2>
			</ServiceDeskAppBase>
		</UFOSegment>
	);
};

export const AssetsAppCmdbObjecV2View = () => {
	return (
		<UFOSegment name="cmdb-object">
			<AssetsAppBase appName={APP_NAMES.INSIGHT_OBJECT_VIEW}>
				{({ workspaceId }) => (
					<FullPageObjectViewWrapper>
						<InsightObjectViewPageContainer>
							{({ prefetchData, objectId }) => (
								<ConditionalObjectViewPage
									workspaceId={workspaceId}
									objectId={objectId}
									prefetchData={prefetchData}
								/>
							)}
						</InsightObjectViewPageContainer>
					</FullPageObjectViewWrapper>
				)}
			</AssetsAppBase>
		</UFOSegment>
	);
};

export const CmdbObjecV2View = () => {
	if (fg('assets_as_an_app_v2')) {
		return <AssetsAppCmdbObjecV2View />;
	}
	return <LegacyCmdbObjecV2View />;
};

export const ConditionalRedirectOrRouteView = () => {
	const [objectId] = usePathParam('objectId');
	const [{ location, route }] = useRouter();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (isInsightRoute(location.pathname) && objectId) {
		fireOperationalAnalytics(
			createAnalyticsEvent({ action: 'redirected', actionSubject: 'routeView' }),
			'jsmCmdbAssetsRouteRedirect object',
			{ redirectedFrom: route.path, redirectedTo: `${getRootPath()}/object/:objectId` },
		);
		return <Redirect to={createObjectDetailUrl(toCmdbObjectId(objectId))} />;
	}

	return <CmdbObjecV2View />;
};

export default ConditionalRedirectOrRouteView;
